import React, { useState, useEffect, useContext } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import { 
  Plane, 
  MapPin, 
  Trophy, 
  Users, 
  Clock, 
  Hotel, 
  Bus,
  User,
  Sparkles,
  CheckCircle,
  Mountain,
  Palmtree,
  Building2,
  Edit,
  Save,
  X
} from 'lucide-react';

const TrainingCampsPage = () => {
  const { API, user } = useContext(AuthContext);
  const [campData, setCampData] = useState({});
  const [editingCamp, setEditingCamp] = useState(null);
  const [editDates, setEditDates] = useState('');
  const [editDuration, setEditDuration] = useState('');
  const [editPriceSingle, setEditPriceSingle] = useState('');
  const [editPriceDouble, setEditPriceDouble] = useState('');
  
  const isAdmin = ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(user?.email);

  // טעינת נתוני מחנות מהשרת
  useEffect(() => {
    const fetchCampData = async () => {
      try {
        const response = await axios.get(`${API}/training-camps`);
        setCampData(response.data);
      } catch (error) {
        console.error('Failed to fetch camp data:', error);
      }
    };
    fetchCampData();
  }, [API]);

  // שמירת עריכה
  const handleSaveEdit = async (campId) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const priceString = editPriceSingle && editPriceDouble 
        ? `${editPriceSingle} לאדם בחדר יחיד | ${editPriceDouble} לאדם בחדר זוגי`
        : editPriceSingle || editPriceDouble || '';
      
      await axios.put(
        `${API}/admin/training-camps/${campId}`,
        { 
          dates: editDates,
          duration: editDuration,
          price: priceString
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // עדכון הסטייט המקומי
      setCampData(prev => ({
        ...prev,
        [campId]: {
          ...prev[campId],
          dates: editDates,
          duration: editDuration,
          price: priceString
        }
      }));
      
      setEditingCamp(null);
      toast.success('המחנה עודכן בהצלחה!');
    } catch (error) {
      console.error('Failed to update camp:', error);
      toast.error('שגיאה בעדכון המחנה');
    }
  };

  // התחלת עריכה
  const startEditing = (campId, currentDates, currentPrice, currentDuration) => {
    setEditingCamp(campId);
    setEditDates(currentDates || '');
    setEditDuration(currentDuration || '');
    
    // פירוק המחיר לחדר יחיד וזוגי
    if (currentPrice && currentPrice.includes('|')) {
      const parts = currentPrice.split('|');
      setEditPriceSingle(parts[0]?.trim() || '');
      setEditPriceDouble(parts[1]?.trim() || '');
    } else {
      setEditPriceSingle(currentPrice || '');
      setEditPriceDouble('');
    }
  };

  const camps = [
    {
      id: 'belgrade',
      city: 'בלגרד',
      country: 'סרביה',
      icon: Trophy,
      color: 'from-red-500 to-blue-600',
      image: '🏛️',
      images: [
        'https://images.unsplash.com/photo-1604259011171-2343696f776b',
        'https://images.unsplash.com/photo-1711970908551-6bb1f39377b5',
        'https://images.unsplash.com/photo-1713132508337-4e06156b544f'
      ],
      description: 'עיר הטניס האירופית - בירת מדינת הטניס של נובאק ג\'וקוביץ\'',
      background: {
        title: 'למה בלגרד?',
        content: 'בלגרד היא בירת סרביה, מדינה עם מסורת טניס עשירה שהעניקה לעולם אלופים כמו נובאק ג\'וקוביץ\'. העיר מציעה שילוב מושלם של היסטוריה, תרבות ספורט, וחיי לילה תוססים. האקלים הים-תיכוני והמתקנים המקצועיים הופכים אותה ליעד מושלם למחנה אימונים.',
        highlights: [
          'מזג אויר אידיאלי לטניס רוב השנה',
          'מתקני טניס ברמה עולמית',
          'עלות מחיה נמוכה יחסית',
          'סביבה תחרותית עם שחקנים ברמה גבוהה'
        ]
      },
      package: {
        title: 'מה כולל המחנה',
        duration: '4 לילות - 5 ימים (סופ״ש)',
        defaultDates: '6-9 בפברואר 2026 (שישי-שני)',
        hotel: 'מלון 4-5 כוכבים במרכז העיר',
        features: [
          '10 אימוני טניס עם מאמנים בכירים',
          'הסעות לצרכי הקבוצה',
          'מאמן מלווה מישראל',
          '2 אטרקציות',
          'חצי פנסיון',
          'כולל ארוחת ערב במסעדה של נובאק ג\'וקוביץ\' בעיר'
        ],
        defaultPricing: {
          single: '1,500€ לאדם בחדר יחיד',
          double: '1,300€ לאדם בחדר זוגי'
        }
      },
      attractions: {
        title: 'אטרקציות וחוויות',
        items: [
          'סיור במוזיאון הטניס הסרבי',
          'ביקור במועדון הילדות של ג\'וקוביץ\'',
          'שייט בנהר הדנובה',
          'סיור בעיר העתיקה של בלגרד',
          'ערב בשכונת Skadarlija התוססת',
          'ביקור במבצר Kalemegdan'
        ]
      }
    },
    {
      id: 'dubai',
      city: 'דובאי',
      country: 'איחוד האמירויות',
      icon: Building2,
      color: 'from-amber-400 to-orange-600',
      image: '🏜️',
      images: [
        'https://images.unsplash.com/photo-1642716540228-d79545ffeb28',
        'https://images.unsplash.com/photo-1643904736472-8b77e93ca3d7',
        'https://images.unsplash.com/photo-1651063820152-d3e7a27b4d2b'
      ],
      description: 'יוקרה, מצוינות וחדשנות - מתקני הטניס המתקדמים בעולם',
      background: {
        title: 'למה דובאי?',
        content: 'דובאי היא בירת היוקרה והספורט של המזרח התיכון. העיר מארחת את טורניר דובאי לטניס (ATP 500) ומציעה מתקנים מהמתקדמים בעולם. האקלים החם (אך יבש), המלונות היוקרתיים, ורמת השירות הגבוהה הופכים את דובאי ליעד פרימיום למחנות אימונים.',
        highlights: [
          'מתקני טניס ברמה של טורנירי ATP',
          'מלונות 5 כוכבים יוקרתיים',
          'אקלים חם ויבש (מתאים לחורף)',
          'חוויית יוקרה ושירות ברמה עולמית',
          'קרבה לישראל (טיסה קצרה)'
        ]
      },
      package: {
        title: 'מה כולל המחנה',
        duration: '4 לילות - 5 ימים (סופ״ש)',
        defaultDates: '',
        hotel: 'מלון 4-5 כוכבים במרכז העיר',
        features: [
          '8 שעות טניס עם מאמנים בכירים',
          'הסעות לצרכי הקבוצה',
          'מאמן מלווה מישראל',
          '2 אטרקציות'
        ],
        defaultPricing: {
          single: '',
          double: ''
        }
      },
      attractions: {
        title: 'אטרקציות וחוויות',
        items: [
          'עליה לבורג\' ח\'ליפה - הבניין הגבוה בעולם',
          'קניות ב-Dubai Mall ו-Mall of Emirates',
          'ספארי במדבר עם רכיבה על גמלים',
          'שייט ביאכטה במרינה של דובאי',
          'ביקור באיי התמרים (Palm Jumeirah)',
          'פארק המים Wild Wadi',
          'ערב בשוק הזהב והתבלינים'
        ]
      }
    },
    {
      id: 'montenegro',
      city: 'מונטנגרו',
      country: 'מונטנגרו',
      icon: Mountain,
      color: 'from-green-500 to-blue-600',
      image: '⛰️',
      images: [
        'https://images.unsplash.com/photo-1715685484548-714978a82166',
        'https://images.unsplash.com/photo-1641234562746-d7c1f02620c1',
        'https://images.unsplash.com/photo-1697547287849-b9bfe3f99e0f'
      ],
      description: 'פנינת הבלקן - שילוב של טניס, טבע עוצר נשימה וים תיכוני',
      background: {
        title: 'למה מונטנגרו?',
        content: 'מונטנגרו (הר השחור) היא פנינה נסתרת בחוף הים התיכון. המדינה מציעה שילוב ייחודי של טניס ברמה גבוהה, חופים מדהימים, והרים מרהיבים. האקלים הים-תיכוני, המתקנים המצוינים, והאווירה הרגועה הופכים אותה ליעד מושלם למחנה אימונים משולב עם חופשה.',
        highlights: [
          'שילוב של טניס וחופשה בים התיכון',
          'מזג אויר מושלם באביב וסתיו',
          'נופים עוצרי נשימה - הרים וים',
          'מחירים אטרקטיביים',
          'סביבה רגועה ונעימה לאימונים'
        ]
      },
      package: {
        title: 'מה כולל המחנה',
        duration: '4 לילות - 5 ימים (סופ״ש)',
        defaultDates: '',
        hotel: 'מלון 4-5 כוכבים במרכז העיר',
        features: [
          '8 שעות טניס עם מאמנים בכירים',
          'הסעות לצרכי הקבוצה',
          'מאמן מלווה מישראל',
          '2 אטרקציות'
        ],
        defaultPricing: {
          single: '',
          double: ''
        }
      },
      attractions: {
        title: 'אטרקציות וחוויות',
        items: [
          'סיור בעיר העתיקה של קוטור (אתר UNESCO)',
          'שייט במפרץ קוטור המרהיב',
          'טיול להרי Durmitor הלאומיים',
          'ביקור באי Sveti Stefan האגדי',
          'שחייה בחופי Budva המפורסמים',
          'סיור במערות Lipa Cave',
          'ערב בעיר העתיקה עם מסעדות מקומיות'
        ]
      }
    }
  ];

  const generalFeatures = [
    { icon: Clock, text: '10 אימוני טניס עם מאמנים בכירים', color: 'text-blue-600' },
    { icon: Hotel, text: 'מלון 4-5 כוכבים במרכז העיר', color: 'text-purple-600' },
    { icon: Bus, text: 'הסעות לכל הצרכים', color: 'text-green-600' },
    { icon: User, text: 'מאמן ישראלי מלווה', color: 'text-orange-600' },
    { icon: Sparkles, text: 'אטרקציות וחוויות', color: 'text-pink-600' }
  ];

  // קבלת תאריכים - מהדאטאבייס או ברירת מחדל
  const getCampDates = (campId, defaultDates) => {
    return campData[campId]?.dates || defaultDates || 'תאריכים יפורסמו בקרוב';
  };

  // קבלת משך זמן - מהדאטאבייס או ברירת מחדל
  const getCampDuration = (campId, defaultDuration) => {
    return campData[campId]?.duration || defaultDuration || '4 לילות - 5 ימים';
  };

  // קבלת מחירים - מהדאטאבייס או ברירת מחדל
  const getCampPrice = (campId, defaultPricing) => {
    const dbPrice = campData[campId]?.price;
    if (dbPrice) return dbPrice;
    if (defaultPricing?.single && defaultPricing?.double) {
      return `${defaultPricing.single} | ${defaultPricing.double}`;
    }
    return 'מחירים יפורסמו בקרוב';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <Navbar />
      <div className="py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Plane className="w-12 h-12 text-blue-600 animate-bounce" />
            <h1 className="text-4xl md:text-5xl font-black text-gray-900">
              מחנות אימונים לחו&quot;ל
            </h1>
            <Plane className="w-12 h-12 text-blue-600 animate-bounce" style={{ transform: 'scaleX(-1)' }} />
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            חווית אימונים בינלאומית במיטב המתקנים בעולם
            <br />
            שלושה יעדים מובחרים לשדרוג המשחק שלך לרמה הבאה
          </p>
        </div>

        {/* General Features */}
        <Card className="mb-12 bg-gradient-to-r from-blue-600 to-purple-600 border-none text-white">
          <CardHeader>
            <CardTitle className="text-center text-2xl text-white flex items-center justify-center gap-2">
              <Trophy className="w-8 h-8" />
              מה כולל כל מחנה?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {generalFeatures.map((feature, idx) => (
                <div key={idx} className="flex flex-col items-center text-center bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <feature.icon className="w-10 h-10 mb-2 text-yellow-300" />
                  <p className="text-sm font-semibold">{feature.text}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Camps */}
        <div className="space-y-16">
          {camps.map((camp, idx) => {
            const Icon = camp.icon;
            const currentDates = getCampDates(camp.id, camp.package.defaultDates);
            const currentDuration = getCampDuration(camp.id, camp.package.duration);
            const currentPrice = getCampPrice(camp.id, camp.package.defaultPricing);
            
            return (
              <Card key={camp.id} className="overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-300">
                {/* Camp Header */}
                <div className={`bg-gradient-to-r ${camp.color} p-8 text-white`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="text-6xl">{camp.image}</div>
                      <div>
                        <h2 className="text-4xl font-black mb-2">{camp.city}</h2>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-5 h-5" />
                          <span className="text-xl">{camp.country}</span>
                        </div>
                      </div>
                    </div>
                    <Icon className="w-20 h-20 opacity-30" />
                  </div>
                  <p className="text-lg font-medium">{camp.description}</p>
                </div>

                <CardContent className="p-8 space-y-8">
                  {/* Background */}
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <MapPin className="w-6 h-6 text-blue-600" />
                      {camp.background.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed mb-4">{camp.background.content}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {camp.background.highlights.map((highlight, i) => (
                        <div key={i} className="flex items-center gap-2 text-gray-700">
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                          <span>{highlight}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Image Gallery */}
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Sparkles className="w-6 h-6 text-purple-600" />
                      גלריית תמונות
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {camp.images.map((img, i) => (
                        <img 
                          key={i} 
                          src={img} 
                          alt={`${camp.city} ${i + 1}`}
                          className="w-full h-48 object-cover rounded-lg shadow-lg hover:scale-105 transition-transform duration-300"
                        />
                      ))}
                    </div>
                  </div>

                  {/* Package Details */}
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Sparkles className="w-6 h-6 text-blue-600" />
                      {camp.package.title}
                      
                      {/* כפתור עריכה לאדמין */}
                      {isAdmin && editingCamp !== camp.id && (
                        <Button
                          onClick={() => startEditing(camp.id, currentDates, currentPrice, currentDuration)}
                          variant="outline"
                          size="sm"
                          className="mr-auto"
                        >
                          <Edit className="w-4 h-4 ml-1" />
                          ערוך
                        </Button>
                      )}
                    </h3>
                    
                    {/* מצב עריכה */}
                    {isAdmin && editingCamp === camp.id ? (
                      <div className="bg-white p-4 rounded-lg border-2 border-blue-300 mb-4">
                        <h4 className="font-bold text-lg mb-3 text-blue-600">✏️ עריכת פרטי המחנה</h4>
                        
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">משך המחנה (לילות/ימים):</label>
                            <Input
                              value={editDuration}
                              onChange={(e) => setEditDuration(e.target.value)}
                              placeholder="לדוגמה: 4 לילות - 5 ימים (סופ״ש)"
                              className="w-full"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">תאריכים:</label>
                            <Input
                              value={editDates}
                              onChange={(e) => setEditDates(e.target.value)}
                              placeholder="לדוגמה: 6-9 בפברואר 2026 (שישי-שני)"
                              className="w-full"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">מחיר חדר יחיד:</label>
                            <Input
                              value={editPriceSingle}
                              onChange={(e) => setEditPriceSingle(e.target.value)}
                              placeholder="לדוגמה: 1,500€ לאדם בחדר יחיד"
                              className="w-full"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">מחיר חדר זוגי:</label>
                            <Input
                              value={editPriceDouble}
                              onChange={(e) => setEditPriceDouble(e.target.value)}
                              placeholder="לדוגמה: 1,300€ לאדם בחדר זוגי"
                              className="w-full"
                            />
                          </div>
                          
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleSaveEdit(camp.id)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Save className="w-4 h-4 ml-1" />
                              שמור
                            </Button>
                            <Button
                              onClick={() => setEditingCamp(null)}
                              variant="outline"
                            >
                              <X className="w-4 h-4 ml-1" />
                              ביטול
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : null}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                        <Clock className="w-6 h-6 text-blue-600" />
                        <span className="font-semibold">{currentDuration}</span>
                      </div>
                      <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                        <MapPin className="w-6 h-6 text-red-600" />
                        <span className="font-semibold">{currentDates}</span>
                      </div>
                      <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                        <Hotel className="w-6 h-6 text-purple-600" />
                        <span className="font-semibold">{camp.package.hotel}</span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                      {camp.package.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* מחירים */}
                    <div className="bg-gradient-to-r from-green-100 to-blue-100 p-4 rounded-lg mt-4">
                      <h4 className="font-bold text-lg mb-2 text-gray-800">💰 מחירים</h4>
                      <p className="text-gray-700 font-medium">{currentPrice}</p>
                    </div>
                  </div>

                  {/* Attractions */}
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Sparkles className="w-6 h-6 text-pink-600" />
                      {camp.attractions.title}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {camp.attractions.items.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 bg-gradient-to-r from-pink-50 to-purple-50 p-3 rounded-lg">
                          <span className="text-xl">🎯</span>
                          <span className="text-gray-700">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Registration CTA */}
                  <div className="text-center pt-4">
                    <a 
                      href={`/camp-registration/${camp.id}`}
                      className="inline-block"
                    >
                      <Button className={`bg-gradient-to-r ${camp.color} hover:opacity-90 text-white text-xl px-12 py-6 rounded-full shadow-lg transform hover:scale-105 transition-all duration-300`}>
                        <Plane className="w-6 h-6 ml-2" />
                        הרשמה למחנה {camp.city}
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Contact Section */}
        <Card className="mt-12 bg-gradient-to-r from-gray-800 to-gray-900 text-white">
          <CardContent className="p-8 text-center">
            <h2 className="text-3xl font-bold mb-4">יש לכם שאלות?</h2>
            <p className="text-lg mb-6">
              צרו קשר ונשמח לעזור בכל שאלה
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:+972544822788">
                <Button variant="outline" className="text-white border-white hover:bg-white hover:text-gray-900">
                  📞 התקשרו אלינו
                </Button>
              </a>
              <a href="https://wa.me/972544822788" target="_blank" rel="noopener noreferrer">
                <Button className="bg-green-500 hover:bg-green-600 text-white">
                  💬 WhatsApp
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
      </div>
    </div>
  );
};

export default TrainingCampsPage;
